// The kit's motion, with the reduced-motion guard welded on.
//
// PLAN-UI §28.1 asks for one motion policy and `prefers-reduced-motion`
// respected *globally*. Tokens are inert data and cannot ask the browser
// anything, so the query lives here — and every primitive builds its
// `transition` through `motion()`, which emits the guard in the same object.
// A hand-written `transition:` string is exactly what this file exists to
// stop: the guard is the half that gets forgotten, so it is not optional.

import { type CSSObject, keyframes } from "@mui/material/styles";

import { TOKENS } from "./tokens";

/** The query every animated primitive must answer. */
export const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

/** Both kinds off — a keyframe animation is motion too. */
export const STILL: CSSObject = { transition: "none", animation: "none" };

/**
 * A transition, plus the reduced-motion guard.
 *
 * `bounced` takes `TOKENS.motion.easeBounce`, `plain` takes the browser's
 * default ease. The split is the sheets' own: they write
 * `transform .16s var(--ease), background .16s, border-color .16s` — the
 * overshoot belongs to geometry, and a colour that overshoots on its way to
 * brand reads as a flicker.
 */
export function motion(
  bounced: string[],
  plain: string[] = [],
  ms: number = TOKENS.motion.duration.fast
): CSSObject {
  const spec = [
    ...bounced.map((prop) => `${prop} ${ms}ms ${TOKENS.motion.easeBounce}`),
    ...plain.map((prop) => `${prop} ${ms}ms`)
  ];
  return { transition: spec.join(", "), [REDUCED_MOTION]: STILL };
}

/**
 * A brand ring breathing out from a control the game is waiting on — End
 * turn once the moves are spent. `color-mix` off the token, so no hex is
 * written twice. Pair it with `[REDUCED_MOTION]: STILL`, as `beckoning` does.
 */
const beckon = keyframes({
  "0%": { boxShadow: `0 0 0 0 color-mix(in srgb, ${TOKENS.color.brand} 65%, transparent)` },
  "70%": { boxShadow: `0 0 0 10px color-mix(in srgb, ${TOKENS.color.brand} 0%, transparent)` },
  "100%": { boxShadow: `0 0 0 0 color-mix(in srgb, ${TOKENS.color.brand} 0%, transparent)` }
});

/** The beckon, guarded: a control that wants pressing, still under reduced motion. */
export const beckoning: CSSObject = {
  animation: `${beckon} 1.6s ease-out infinite`,
  [REDUCED_MOTION]: STILL
};
